import {
  Badge,
  Checkbox,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
  Tooltip,
  Icon,
  InputGroup,
  InputLeftElement,
  Spinner,
} from "@chakra-ui/react";
import InputWithLabel from "../InputWithLabel";
import { useFormContext } from "react-hook-form";
import CurrencyInput from "react-currency-input-field";
import { TicketTypeValues } from "../schema";
import { useState, useEffect } from "react";
import actions from "../../../../../actions";
import { FiInfo } from "react-icons/fi";
import { dineroFormatCurrency } from "src/utils";

const SingleAllocation = ({
  currency,
  loading = false,
  eventId,
}: {
  currency: string;
  loading?: boolean;
  eventId: string;
}) => {
  const {
    register,
    watch,
    formState: { errors, defaultValues },
    setValue,
    getValues,
  } = useFormContext<TicketTypeValues>();

  const isFree = watch("free");
  const isEdit = !!watch("id");
  const price = watch("price");
  const soldOut = !!watch("sold_out");
  const totalSold = watch("totalSold");
  const currentQuantity = watch("quantity");
  const originalQuantity =
    (defaultValues as any)?.originalQuantity ??
    (defaultValues as any)?.quantity;
  const quantityDelta =
    typeof currentQuantity === "number" && typeof originalQuantity === "number"
      ? currentQuantity - originalQuantity
      : 0;
  const willBeSoldOut =
    typeof totalSold === "number" &&
    typeof currentQuantity === "number" &&
    currentQuantity === totalSold;

  const [priceBreakdown, setPriceBreakdown] = useState<{
    service_fee: number;
    processing_fee: number;
    rebate_fee: number;
    final_fees: number;
    final_price: number;
  } | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    setIsCalculating(true);
    const calculatePrice = async () => {
      if (!price || !eventId || isFree || isNaN(parseFloat(price))) {
        setPriceBreakdown(null);
        return;
      }

      try {
        const { total_price, total_fees, ticket_fees } =
          await actions.ADMIN.calculateFinalTicketPrice(
            parseFloat(price),
            eventId
          );
        setPriceBreakdown({
          service_fee:
            ticket_fees.find((fee) => fee.type === "service_fee")?.price
              .amount || 0,
          processing_fee:
            ticket_fees.find((fee) => fee.type === "processing_fee")?.price
              .amount || 0,
          rebate_fee:
            ticket_fees.find((fee) => fee.type === "rebate")?.price.amount || 0,
          final_price: total_price.getAmount() || 0,
          final_fees: total_fees.getAmount() || 0,
        });
      } catch (error) {
        console.error("Error calculating price:", error);
        setPriceBreakdown(null);
      } finally {
        setIsCalculating(false);
      }
    };

    const timeoutId = setTimeout(calculatePrice, 300);
    return () => clearTimeout(timeoutId);
  }, [price, eventId, isFree]);

  const increment = watch("increment", 1);
  const incrementAboveOne = increment > 1;
  return (
    <VStack w="full" spacing={0}>
      <HStack w="full">
        <Stack w="50%">
          {isEdit ? null : (
            <HStack
              alignItems={"center"}
              alignSelf={"flex-end"}
              height="22px"
              top="30px"
              mt="-30px"
              position="relative"
            >
              <Checkbox
                borderRadius="6px"
                borderColor="#E8E8E8"
                {...register("free", {
                  disabled: loading,
                  onChange: (e) => {
                    const isTrue = e.target.checked;
                    if (isTrue)
                      setValue("price", undefined, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                  },
                })}
              />
              <Text>Free</Text>
            </HStack>
          )}
          <InputWithLabel
            name="price"
            label={incrementAboveOne ? `Price per ticket` : `Price`}
            //@ts-ignore
            required
            renderInput={({ field, errors }) => (
              <>
                <CurrencyInput
                  //@ts-ignore
                  backgroundColor="white"
                  height="48px"
                  border="1px solid #E8E8E8"
                  borderRadius="8px"
                  boxShadow="0px 2px 6px rgba(0,0,0,0.035)"
                  borderColor={errors.price ? "red" : "inherit"}
                  borderWidth={errors.price ? 2 : 1}
                  style={{ width: "100%" }}
                  disabled={field.disabled || isFree || loading}
                  placeholder={`$00.00`}
                  defaultValue={field.value}
                  value={field.value}
                  onValueChange={(value) => {
                    setValue(`price`, value || "", {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  intlConfig={{
                    locale: "en-US",
                    currency,
                  }}
                  customInput={Input}
                  step={1}
                />
              </>
            )}
          />
        </Stack>
        <Stack w="50%">
          <Text
            fontSize="sm"
            color="gray.500"
            textAlign="right"
            top="30px"
            mt="-30px"
            position="relative"
          >
            (Max 100)
          </Text>
          <InputWithLabel
            required
            name="limit"
            label="Limit"
            renderInput={({ field }) => (
              <Input
                name={field.name}
                //@ts-ignore
                height="48px"
                backgroundColor="white"
                border="1px solid #E8E8E8"
                borderRadius="8px"
                boxShadow="0px 2px 6px rgba(0,0,0,0.035)"
                required
                placeholder={"Limit"}
                isInvalid={!!errors["limit"]}
                isDisabled={field.disabled || loading}
                //@ts-ignore
                value={field.value}
                type="number"
                max={100}
                min={1}
                {...field}
              />
            )}
          ></InputWithLabel>
        </Stack>
      </HStack>
      <HStack w="100%">
        <HStack w="50%">
          <InputWithLabel
            required
            inputProps={{
              disabled: loading,
            }}
            name="quantity"
            label="Quantity"
            labelAddon={
              soldOut ? <Badge colorScheme="orange">Sold out</Badge> : null
            }
            renderInput={({ field }) => (
              <Input
                height="48px"
                placeholder={"Quantity"}
                isInvalid={!!errors["quantity"]}
                isDisabled={field.disabled || loading}
                type="number"
                min={1}
                {...field}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  if (value === "") return field.onChange(NaN);
                  const n = parseInt(value, 10);
                  field.onChange(Number.isNaN(n) ? undefined : n);
                }}
              />
            )}
          />
        </HStack>
        <HStack
          w="50%"
          height={"full"}
          justifyContent={"flex-start"}
          alignItems={"flex-start"}
        >
          <InputWithLabel
            required
            inputProps={{
              disabled: loading,
            }}
            renderInput={({ field }) => (
              <Input
                height="48px"
                name={field.name}
                required
                placeholder={"Increment"}
                isInvalid={!!errors["increment"]}
                isDisabled={field.disabled || loading}
                //@ts-ignore
                value={field.value}
                type="number"
                max={100}
                min={1}
                {...field}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setValue("increment", parseInt(value), {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            )}
            name="increment"
            //@ts-ignore
            defaultValue={1}
            label="Increment"
            placeholder="Increment"
          />
        </HStack>
      </HStack>
      {!errors["quantity"] &&
      !Number.isNaN(quantityDelta) &&
      quantityDelta > 0 &&
      soldOut ? (
        <Text fontSize="xs" color="gray.600" lineHeight={4}>
          Adding {quantityDelta} tickets will return this ticket type to{" "}
          <Badge colorScheme="green">On Sale</Badge> status.
        </Text>
      ) : null}
      {!soldOut && willBeSoldOut ? (
        <Text fontSize="xs" color="gray.600" lineHeight={4}>
          This will move this ticket type to the{" "}
          <Badge colorScheme="orange">Sold out</Badge> status.
        </Text>
      ) : null}
      {price && !isNaN(parseFloat(price)) && !isFree && (
        <HStack
          w="full"
          spacing={0}
          backgroundColor="gray.50"
          color="gray.500"
          mt="12px"
          p={4}
          borderRadius="md"
          alignItems="stretch"
        >
          <HStack w="50%" justifyContent="space-between">
            <Text fontSize="sm" fontWeight="medium">
              Price Breakdown
            </Text>
          </HStack>
          <HStack w="50%" justifyContent="space-between">
            {isCalculating ? (
              <HStack w="full" justifyContent="flex-end">
                <Spinner size="sm" color="gray.400" thickness="2px" />
                <Text fontSize="xs" color="gray.500">
                  Loading price breakdown...
                </Text>
              </HStack>
            ) : (
              priceBreakdown && (
                <>
                  <Text fontSize="xs" color="gray.600">
                    Price: {dineroFormatCurrency(parseFloat(price) * 100)}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Fees: {dineroFormatCurrency(priceBreakdown?.final_fees)}{" "}
                    <Tooltip
                      label={
                        <>
                          {priceBreakdown?.service_fee > 0 && (
                            <>
                              Service fee:{" "}
                              {dineroFormatCurrency(
                                priceBreakdown?.service_fee
                              )}
                            </>
                          )}
                          {priceBreakdown?.processing_fee > 0 && (
                            <>
                              <br />
                              Processing fee:{" "}
                              {dineroFormatCurrency(
                                priceBreakdown?.processing_fee
                              )}
                            </>
                          )}
                          {priceBreakdown?.rebate_fee > 0 && (
                            <>
                              <br />
                              Rebate Fee:{" "}
                              {dineroFormatCurrency(priceBreakdown?.rebate_fee)}
                            </>
                          )}
                        </>
                      }
                      placement="bottom"
                    >
                      <span>
                        <Icon as={FiInfo} boxSize={3} color="gray.500" />
                      </span>
                    </Tooltip>
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    Fan Price:{" "}
                    {dineroFormatCurrency(priceBreakdown?.final_price)}
                  </Text>
                </>
              )
            )}
          </HStack>
        </HStack>
      )}
    </VStack>
  );
};

export default SingleAllocation;
